import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import type { EmailTone } from '../../types/customerFollowUp/enums';

interface Options {
    /** The draft is loaded, without error, and no AI run is writing it. */
    ready: boolean;
    /** Set while a falling-edge reload is about to overwrite whatever this hook would produce. */
    reloadPendingRef: RefObject<boolean>;
    noAi: boolean;
    tone: EmailTone;
    /** The custom template is the company's own wording: neither No-AI nor a regeneration replaces it. */
    customTemplateActive: boolean;
    /** The saved draft loaded with an empty body. */
    needsInitialRegenRef: RefObject<boolean>;
    refreshBody: (tone: EmailTone, noAi: boolean) => void;
    /** Shows the cached AI body for the tone and returns true, or returns false when none is cached. */
    showCachedAiBody: (tone: EmailTone) => boolean;
}

/**
 * Keeps the email body in step with the No-AI mode: once the draft has loaded, an empty body or No-AI
 * regenerates it, and each later toggle swaps the AI and No-AI bodies.
 */
export const useNoAiBodySync = ({ ready, reloadPendingRef, noAi, tone, customTemplateActive, needsInitialRegenRef, refreshBody, showCachedAiBody }: Options) => {
    const hasSyncedInitialRef = useRef(false);
    const previousNoAiRef = useRef(noAi);
    const showCachedAiBodyRef = useRef(showCachedAiBody);
    showCachedAiBodyRef.current = showCachedAiBody;

    useEffect(() => {
        // Regenerating while a run is in flight would race its draft stage; the falling-edge reload re-checks.
        if (!ready || reloadPendingRef.current) return;

        if (customTemplateActive) {
            hasSyncedInitialRef.current = true;
            previousNoAiRef.current = noAi;
            return;
        }

        if (!hasSyncedInitialRef.current) {
            hasSyncedInitialRef.current = true;
            previousNoAiRef.current = noAi;
            if (noAi || needsInitialRegenRef.current) refreshBody(tone, noAi);
            return;
        }

        const wasNoAi = previousNoAiRef.current;
        previousNoAiRef.current = noAi;
        if (wasNoAi === noAi) return;

        // AI → No-AI: an AI-generated body must never linger, so always regenerate. The other way, a cached
        // AI body is reused instead of paying for a regeneration.
        if (noAi || !showCachedAiBodyRef.current(tone)) refreshBody(tone, noAi);
    }, [ready, reloadPendingRef, noAi, tone, customTemplateActive, needsInitialRegenRef, refreshBody]);
};
